import { Box, CircularProgress, Tab, Tabs } from '@mui/material';
import React, { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  selectMediaLoading,
  selectSelectedTab,
  selectSelectedTabFiles,
  setSelectedTab,
} from '../../../store/slices/mediaSlice';
import { fetchMediaFiles } from '../../../store/thunks/mediaThunks';
import type { ProductContentProps } from '../../../types/common.types';

import { MediaContent } from './MediaContent';

export const ProductContent: React.FC<ProductContentProps> = ({ product, tabs }) => {
  const dispatch = useAppDispatch();
  const selectedTab = useAppSelector(selectSelectedTab);
  const selectedTabFiles = useAppSelector(selectSelectedTabFiles);
  const loading = useAppSelector(selectMediaLoading);

  useEffect(() => {
    dispatch(setSelectedTab(tabs?.[0]?.label || ''));
  }, [product.productcode, tabs, dispatch]);

  useEffect(() => {
    if (selectedTab) {
      dispatch(fetchMediaFiles(selectedTab));
    }
  }, [selectedTab, dispatch]);

  if (!tabs.length || !product) {
    return null;
  }
  // Filter tabs for current product
  const productTabs = tabs.filter((tab) => tab.productcode === product.productcode);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    dispatch(setSelectedTab(tabs[newValue].label));
  };

  // Find the selected tab index, defaulting to 0 if not found
  const selectedTabIndex =
    tabs.findIndex((tab) => tab.label === selectedTab) > 0
      ? tabs.findIndex((tab) => tab.label === selectedTab)
      : 0;

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={selectedTabIndex} onChange={handleTabChange}>
          {productTabs.map((tab, _index) => (
            <Tab key={tab.folder} label={tab.label} />
          ))}
        </Tabs>
      </Box>
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          p={2}
          data-testid="loading-spinner"
        >
          <CircularProgress size={40} />
        </Box>
      ) : (
        <MediaContent
          mediaFiles={selectedTabFiles.mediaFiles}
          textFiles={selectedTabFiles.textFiles}
        />
      )}
    </Box>
  );
};
